import React, { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config/api';

/**
 * FirmTagsManager — manage the firm-entity tag vocabulary (create/edit/delete,
 * assign colors). Rendered as the "Tags" tab on the Firms page. Uses the
 * dedicated firm tag endpoints so it never touches candidate tags.
 *   GET/POST/PUT/DELETE /api/firms/tags/vocab
 */

const COLORS = [
    { name: 'Emerald', value: '#059669' },
    { name: 'Blue', value: '#2563EB' },
    { name: 'Purple', value: '#7C3AED' },
    { name: 'Amber', value: '#D97706' },
    { name: 'Pink', value: '#DB2777' },
    { name: 'Indigo', value: '#4F46E5' },
    { name: 'Teal', value: '#0D9488' },
    { name: 'Red', value: '#DC2626' },
    { name: 'Gray', value: '#6B7280' },
];

export default function FirmTagsManager() {
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [searchFocused, setSearchFocused] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: '', color: '#059669' });
    const [saving, setSaving] = useState(false);

    const token = localStorage.getItem('authToken');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => { loadTags(); /* eslint-disable-next-line */ }, []);

    const loadTags = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/firms/tags/vocab`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) setTags(await res.json());
        } catch (e) { console.error('Error loading firm tags:', e); }
        finally { setLoading(false); }
    };

    const openCreate = () => { setEditing(null); setForm({ name: '', color: '#059669' }); setShowForm(true); };
    const openEdit = (tag) => { setEditing(tag); setForm({ name: tag.name, color: tag.color || '#6B7280' }); setShowForm(true); };
    const closeForm = () => { setShowForm(false); setEditing(null); };

    const handleSave = async () => {
        if (!form.name.trim()) return;
        try {
            setSaving(true);
            if (editing) {
                const res = await fetch(`${API_URL}/firms/tags/vocab/${editing.id}`, { method: 'PUT', headers, body: JSON.stringify(form) });
                if (!res.ok) throw new Error('save failed');
            } else {
                const res = await fetch(`${API_URL}/firms/tags/vocab`, { method: 'POST', headers, body: JSON.stringify(form) });
                if (!res.ok) throw new Error('save failed');
            }
            closeForm();
            loadTags();
        } catch (e) { alert('Failed to save tag'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (tag) => {
        if (!window.confirm(`Delete the tag "${tag.name}"? It will be removed from all firms.`)) return;
        try {
            const res = await fetch(`${API_URL}/firms/tags/vocab/${tag.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error('delete failed');
            loadTags();
        } catch (e) { alert('Failed to delete tag'); }
    };

    const filtered = useMemo(() =>
        tags.filter(t => t.name.toLowerCase().includes(search.toLowerCase())).sort((a, b) => a.name.localeCompare(b.name)),
        [tags, search]);

    return (
        <div className="h-full flex flex-col overflow-hidden bg-[color:var(--list-page-bg)]">
            {/* Toolbar */}
            <div className="bg-[color:var(--list-page-bg)] px-3 py-1.5 flex-shrink-0 flex items-center gap-2">
                <div className="relative">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onFocus={() => setSearchFocused(true)}
                        onBlur={() => setSearchFocused(false)}
                        placeholder="Search..."
                        className="h-[30px] w-44 pl-7 pr-2 text-sm border border-gray-300 rounded focus:outline-none transition-shadow"
                        style={searchFocused ? { boxShadow: '0 0 0 1px rgba(16, 185, 129, 0.15), 0 0 14px 2px rgba(16, 185, 129, 0.22), 0 0 28px 3px rgba(16, 185, 129, 0.10)', borderColor: 'rgba(16, 185, 129, 0.4)' } : undefined}
                    />
                    <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                </div>
                <div className="flex-1" />
                <button onClick={openCreate} className="h-[30px] px-3 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    New Tag
                </button>
            </div>

            {/* List block */}
            <div className="flex-1 min-h-0 mx-3 mb-1 bg-[color:var(--list-block-bg)] border rounded-lg shadow-sm overflow-hidden flex flex-col">
                {loading ? (
                    <div className="flex-1 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>
                ) : filtered.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center py-16 px-4">
                        <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z" /></svg>
                        <p className="text-gray-500 text-sm">{search ? 'No tags match your search' : 'No firm tags yet'}</p>
                        {!search && <button onClick={openCreate} className="mt-4 px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700">Create your first tag</button>}
                    </div>
                ) : (
                    <div className="flex-1 min-h-0 overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
                        {filtered.map((tag) => (
                            <div key={tag.id} className="group flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 bg-white/60 hover:bg-emerald-50/50">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${tag.color || '#6B7280'}1f`, color: tag.color || '#6B7280' }}>
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tag.color || '#6B7280' }} />
                                    {tag.name}
                                </span>
                                {tag.description && <span className="text-xs text-gray-500 truncate flex-1">{tag.description}</span>}
                                <div className="flex items-center gap-1 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => openEdit(tag)} className="p-1.5 text-gray-400 hover:text-emerald-600 rounded" title="Edit">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                    </button>
                                    <button onClick={() => handleDelete(tag)} className="p-1.5 text-gray-400 hover:text-red-600 rounded" title="Delete">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create/Edit modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={closeForm}>
                    <div className="bg-white rounded-lg shadow-xl w-96 max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
                        <div className="px-4 py-3 border-b flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900">{editing ? 'Edit Tag' : 'New Tag'}</h3>
                            <button onClick={closeForm} className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                        </div>
                        <div className="p-4 space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                                <input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                                    placeholder="e.g. Distressed Credit"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Color</label>
                                <div className="flex flex-wrap gap-2">
                                    {COLORS.map((c) => (
                                        <button key={c.value} onClick={() => setForm({ ...form, color: c.value })}
                                            className={`w-7 h-7 rounded-full transition-transform ${form.color === c.value ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''}`}
                                            style={{ backgroundColor: c.value }} title={c.name} />
                                    ))}
                                </div>
                            </div>
                            <div className="pt-1">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${form.color}1f`, color: form.color }}>
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: form.color }} />
                                    {form.name || 'Preview'}
                                </span>
                            </div>
                        </div>
                        <div className="px-4 py-3 border-t flex justify-end gap-2">
                            <button onClick={closeForm} className="px-3 h-[30px] text-xs text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                            <button onClick={handleSave} disabled={saving || !form.name.trim()} className="px-3 h-[30px] text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">{saving ? 'Saving…' : editing ? 'Save' : 'Create'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
